import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { ModalContainer } from '../../../common';

import { DirectDepositContent } from './DirectDepositContent';
import { CheckContent } from './CheckContent';

import closeIcon from '../../../../assets/close-button.svg';
import { REIMBURSEMENT_METHODS } from '../../../../constants';

import '../css/ReimbursementEditModal.css';
import { RadioButton } from '../../../common/RadioButton';
import styles from '../css/ReimbursementEditModal.module.css';
import {
  changeReimbursementMethodType,
} from '../../../../actions';
import { ActionButton } from '../../../common/petCloud';
import { useCareCreditReimbursement } from '../../../../hooks/useFeatureFlags';
import { CareCreditContent } from './CareCreditContent';
import { useCareCreditValidation } from '../../../../hooks';

const STEPS = {
  reimbursementDetail: 2,
  selectMethod: 1,
};

const ReimbursementMethodList = ({ preferredMethod, onNextClick }) => {
  const { t } = useTranslation('myAccount');
  const careCreditFlag = useCareCreditReimbursement();
  const { careCredit } = useCareCreditValidation();
  const dispatch = useDispatch();

  const isBankAccountActive = preferredMethod
    === REIMBURSEMENT_METHODS.deposit;

  const isCheckActive = preferredMethod
    === REIMBURSEMENT_METHODS.check;

  const isCareCreditActive = preferredMethod
    === REIMBURSEMENT_METHODS.careCredit;

  const getClassName = useCallback((isActive) => `${styles.radioButton} `
    + `${isActive ? styles.radioButtonSelected : ''}`, []);

  const onRadioButtonChange = useCallback((e) => {
    const { value } = e.target;
    dispatch(changeReimbursementMethodType(Number(value)));
  }, []);

  return (
    <div className={styles.reimbursementMethodContainer}>
      <fieldset>
        <legend className={styles.legend}>
          {t('reimbursementSection.editModal.selectMethod')}
        </legend>

        <RadioButton
          blue
          checked={isCheckActive}
          className={getClassName(isCheckActive)}
          id="reimbursement-check"
          name="reimbursementMethod"
          onChange={onRadioButtonChange}
          value={REIMBURSEMENT_METHODS.check}
        >
          {t('reimbursementSection.editModal.check')}
        </RadioButton>

        <RadioButton
          blue
          checked={isBankAccountActive}
          className={getClassName(isBankAccountActive)}
          id="reimbursement-direct-deposit"
          name="reimbursementMethod"
          onChange={onRadioButtonChange}
          value={REIMBURSEMENT_METHODS.deposit}
        >
          {t('reimbursementSection.editModal.directDeposit')}
        </RadioButton>

        {careCreditFlag && careCredit.hasCareCreditUrl ? (
          <RadioButton
            blue
            checked={isCareCreditActive}
            className={getClassName(isCareCreditActive)}
            id="reimbursement-care-credit"
            name="reimbursementMethod"
            onChange={onRadioButtonChange}
            value={REIMBURSEMENT_METHODS.careCredit}
          >
            {t('reimbursementSection.editModal.careCredit')}
          </RadioButton>
        ) : null}
      </fieldset>

      <div className={styles.actions}>
        <ActionButton
          onClick={onNextClick}
          title={t('reimbursementSection.editModal.next')}
        />
      </div>
    </div>
  );
};

const ReimbursementMethodDetail = ({
  cancelAll = () => { },
  method,
  onCancel,
}) => {
  switch (method) {
    case REIMBURSEMENT_METHODS.deposit:
      return <DirectDepositContent onCancel={onCancel} />;
    case REIMBURSEMENT_METHODS.check:
      return <CheckContent onCancel={onCancel} />;
    case REIMBURSEMENT_METHODS.careCredit:
      return <CareCreditContent cancelAll={cancelAll} onCancel={onCancel} />;
    default:
      return null;
  }
};

const ReimbursementEditModal = ({
  isEditing = false,
  isVisible = false,
  onCancel = () => { },
}) => {
  const { t } = useTranslation('myAccount');
  const store = useSelector(({ billingPayments }) => ({
    billingPayments,
  }), shallowEqual);

  const [step, setStep] = useState(STEPS.selectMethod);
  const {
    billingPayments: {
      reimbursementData: { PreferredReimbursementMethod },
    },
  } = store;

  const goToStep = useCallback((newStep) => () => {
    setStep(newStep);
  }, []);

  useEffect(() => {
    if (isVisible && isEditing) {
      setStep(STEPS.reimbursementDetail);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) {
      setStep(STEPS.selectMethod);
    }
  }, [isVisible]);

  const stepRendered = useMemo(() => {
    switch (step) {
      case STEPS.reimbursementDetail:
        return (
          <ReimbursementMethodDetail
            cancelAll={onCancel}
            method={PreferredReimbursementMethod}
            onCancel={goToStep(STEPS.selectMethod)}
          />
        );
      case STEPS.selectMethod:
      default:
        return (
          <ReimbursementMethodList
            onNextClick={goToStep(STEPS.reimbursementDetail)}
            preferredMethod={PreferredReimbursementMethod}
          />
        );
    }
  }, [step, PreferredReimbursementMethod, goToStep]);

  if (!isVisible) {
    return null;
  }

  return (
    <ModalContainer show>
      <div className={styles.container}>
        <div className={styles.titleContainer}>
          <h3>{t('reimbursementSection.editModal.title')}</h3>

          <button
            className={styles.closeIconContainer}
            id="closeIconEditReimbursementModal"
            onClick={onCancel}
            type="button"
          >
            <img
              alt={t('reimbursementSection.editModal.close')}
              src={closeIcon}
            />
          </button>
        </div>

        {stepRendered}
      </div>
    </ModalContainer>
  );
};

export { ReimbursementEditModal };
